import { WebOfferAdapter } from '@apm/bloom-receiver-sdk';
import type { OfferAdapter, OfferItem, OfferRequest } from '@apm/bloom-receiver-sdk';
import { offersEnabled } from './offers-enabled.js';

/**
 * 상품 어댑터를 만든다 — **데코레이터 하나가 설정 토글의 전부다** (v23, `docs/47` §9.6).
 *
 * ★ **`ReceiverOptions.offers` 를 조건부로 주입하지 않는다** (절대 규칙 60). 그것은
 * 생성자 인자라, 토글 한 번에 리시버를 다시 세워야 하고 그러면 **영상이 처음부터 다시
 * 시작한다**(절대 규칙 25). 언제나 주입하고, **꺼져 있으면 어댑터가 빈 배열을 낸다.**
 *
 * **그 증상이 조용하다는 것이 이 결정의 무게다** — 상품은 정상적으로 멈추므로 **토글
 * 자체는 잘 도는 것처럼 보이고**, 「껐더니 영상이 되감겼다」는 **무대에서만 밟는다**
 * (`docs/47` §14 의 23). 하네스가 `start` 이벤트를 세는 이유가 그것이다.
 *
 * ★★ **호출 시점에 읽는다.** 생성 시점에 `offersEnabled()` 를 읽어 두면 토글이 **다음
 * 채널 전환까지 안 먹는다** — 그리고 그 증상은 「껐는데 상품이 뜬다」 하나다.
 *
 * **빈 배열은 매칭 없음과 같은 길이다.** 코어에 새 분기가 없다 — 관문이 이미 「받아 온
 * 것이 없으면 그 큐는 없었던 것으로 한다」로 처리한다 (`docs/47` §8.6).
 *
 * **코어는 0줄이다.** 상품을 끄는 일이 코어의 관심사가 아니라는 것이 이 설계의 요점이다.
 */
export function createOfferAdapter(inner: OfferAdapter = new WebOfferAdapter()): OfferAdapter {
  return {
    async requestOffer(req: OfferRequest, opts: { timeoutMs: number }): Promise<OfferItem[]> {
      // **여기서 읽는 것이 계약이다.** 위 ★★ 참조.
      if (!offersEnabled()) return [];
      return inner.requestOffer(req, opts);
    },
    track(urls: readonly string[]): void {
      // 꺼져 있으면 쏠 것도 없다 — 애초에 상품이 나가지 않았다.
      if (!offersEnabled()) return;
      inner.track(urls);
    },
  };
}

/**
 * ★ **앱이 쓰는 어댑터는 하나다.**
 *
 * ── 왜 싱글턴인가 ─────────────────────────────────────────
 *
 * 부르는 자리가 **둘**이다 — `useReceiver`(코어에 주입)와 **`WatchScreen`**(카드를 눌렀을
 * 때의 클릭 트래킹, `docs/47` §9.3). 각자 만들면 인스턴스가 둘이고, **그때 토글의 뜻이
 * 갈릴 수 있다**(한쪽이 옛 값을 들고 있는 구조로 바뀌는 날 그렇다).
 *
 * **지금은 둘 다 상태가 없어서 갈리지 않는다** — 그래서 이것은 성능이 아니라 **규율**이다:
 * 「토글을 읽는 자리가 하나」라는 성질을 배선으로 지킨다.
 *
 * **`createOfferAdapter` 는 그대로 남긴다** — 테스트가 안쪽을 갈아 끼운다.
 */
let shared: OfferAdapter | null = null;

export function offerAdapter(): OfferAdapter {
  shared ??= createOfferAdapter();
  return shared;
}

/**
 * 클릭 트래킹을 쏜다. **실패해도 조용하다** (절대 규칙 55 승계).
 *
 * **화면이 어댑터를 직접 들고 있지 않게 하려고 이 얇은 함수를 둔다** — `WatchScreen` 이
 * `OfferAdapter` 타입을 알 이유가 없고, 알면 그 파일이 어댑터 계약에 묶인다.
 */
export function trackOfferClick(urls: readonly string[]): void {
  offerAdapter().track(urls);
}
